/**
 * Opt-in client debug logging (web + Capacitor WebView).
 *
 * Enable in DevTools / Safari Web Inspector:
 *   localStorage.setItem('logictry.debug', '1')
 * Disable:
 *   localStorage.removeItem('logictry.debug')
 * Then reload.
 *
 * Cross-origin `/videoplayer` iframes cannot read the parent localStorage key;
 * use `withDebugQuery(url)` so the parent appends `?vdebug=1` when debug is on.
 */

export const DEBUG_STORAGE_KEY = 'logictry.debug';

export function isDebugEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem(DEBUG_STORAGE_KEY) === '1') return true;
    // Propagated into /videoplayer iframes (and handy one-off URL enable).
    return new URLSearchParams(window.location.search).get('vdebug') === '1';
  } catch {
    return false;
  }
}

/**
 * Structured console log. Silent unless {@link isDebugEnabled}.
 * Single-string JSON so Safari/Web Inspector does not truncate expandable objects.
 *
 * @param {string} tag e.g. 'LiveStreamPanel'
 * @param {string} event
 * @param {unknown} [data]
 */
export function debugLog(tag, event, data) {
  if (!isDebugEnabled()) return;
  try {
    if (data === undefined) {
      console.log(`[${tag}] ${event}`);
      return;
    }
    let serialized;
    try {
      serialized = JSON.stringify(data, (_k, v) => (
        v instanceof Error ? `${v.name}: ${v.message}` : v
      ));
    } catch {
      serialized = String(data);
    }
    console.log(`[${tag}] ${event} ${serialized}`);
  } catch {
    // ignore
  }
}

/**
 * Append `vdebug=1` when debug logging is enabled so cross-origin players inherit the gate.
 * @param {string} url
 * @returns {string}
 */
export function withDebugQuery(url) {
  if (!url || !isDebugEnabled()) return url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    if (u.searchParams.get('vdebug') === '1') return u.toString();
    u.searchParams.set('vdebug', '1');
    return u.toString();
  } catch {
    if (/[?&]vdebug=1(?:&|$)/.test(url)) return url;
    return url.includes('?') ? `${url}&vdebug=1` : `${url}?vdebug=1`;
  }
}
